import VehicleAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity';
import {
  Fleet,
  FleetTypeEntry,
  PlanResponse,
  Shift,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.interface';
import { FleetSizing } from 'app/modules/components/routing/sub-routes/route-optimization/plan-result';
import { capacityLbOf } from 'app/modules/components/routing/sub-routes/route-optimization/subroute-fleet';

/** Mismo factor que el resto del kit; la respuesta del motor viene en kg. */
const KG_TO_LB = 2.20462;

/**
 * Dimensionamiento de la flota que FALTA en un territorio.
 *
 * Existe porque la corrida ahora declara una brecha —territorios con pedidos y
 * sin camión asignado— y "no se puede planificar" no es una respuesta accionable.
 * La accionable es *cuántos camiones, y de qué capacidad, harían falta acá*, que
 * es lo que el modo `fleet` de `POST /plan` responde en `fleet_usage`.
 *
 * Dos reglas que lo mantienen honesto:
 *
 * 1. **Los moldes son la flota que la oficina TIENE.** Ofrecerle al motor una
 *    capacidad inventada devuelve una cifra que no se puede comprar. Cuando la
 *    oficina no tiene ningún camión asignado se usa la de la pantalla y se
 *    declara como supuesto — la cifra sigue sirviendo, pero se sabe de dónde sale.
 * 2. **El resultado NO es un plan.** No hay camión al que asignarle esas rutas,
 *    así que se rotula como estimación y no se puede confirmar (ver el contrato
 *    en `specs/fleet-gap-sizing`).
 *
 * Módulo puro: entra la flota real y la respuesta del motor, sale el modelo de
 * vista. Sin HTTP.
 */

/**
 * Techo absoluto de unidades por tipo.
 *
 * El techo real se deriva de la demanda (ver `sizingFleet`), así que esto es solo
 * una barrera contra un peso disparatado en los datos: sin ella, una orden con el
 * peso mal cargado pediría miles de camiones y el motor recibiría un problema
 * absurdo.
 */
export const SIZING_MAX_TRUCKS_PER_TYPE = 40;

/** Molde de dimensionamiento: una capacidad ofrecida, con su procedencia. */
export interface SizingMold {
  capacityLb: number;
}

export interface SizingFleetResult {
  /** El `fleet` del contrato, listo para la petición. */
  fleet: Fleet;
  /** Capacidades ofrecidas, en libras y sin repetir. */
  molds: SizingMold[];
  /**
   * `true` si la capacidad NO salió de la flota real de la oficina: es un
   * supuesto de pantalla y el resultado tiene que decirlo.
   */
  capacityAssumed: boolean;
}

/**
 * El `fleet` con el que se dimensiona un territorio sin camión.
 *
 * Los moldes son las capacidades DISTINTAS de la flota asignada de la oficina —
 * la mezcla que responde "con los camiones que tenés". Repetir una capacidad no
 * agrega información: el motor elige cuántas unidades usa de cada tipo, así que
 * dos camiones iguales son un tipo con techo 2, no dos tipos.
 *
 * El techo por tipo se deriva de la demanda: `ceil(peso / capacidad) + 1`. Así el
 * techo nunca es la causa de que un pedido quede afuera, y `isMinimum` señala
 * infactibilidad real (jornada, ventanas) en vez de un cap arbitrario.
 *
 * La jornada de los moldes es la de la pantalla: el territorio no tiene camión,
 * así que no hay itinerario del maestro de dónde sacarla. Es parte del supuesto.
 */
export function sizingFleet(
  assignedVehicles: readonly VehicleAttributes[],
  demandLb: number,
  shift: Shift,
  fallbackCapacityLb: number,
): SizingFleetResult {
  const capacities = [
    ...new Set(
      (assignedVehicles ?? [])
        .map((v) => capacityLbOf(v))
        .filter((c): c is number => c !== undefined && c > 0),
    ),
  ].sort((a, b) => a - b);

  const capacityAssumed = capacities.length === 0;
  const molds = (capacityAssumed ? [fallbackCapacityLb] : capacities).map((capacityLb) => ({
    capacityLb,
  }));

  const truck_types: FleetTypeEntry[] = molds.map((m) => ({
    truck_type: { capacity: m.capacityLb, shift },
    max_count: Math.min(
      SIZING_MAX_TRUCKS_PER_TYPE,
      Math.max(1, Math.ceil(Math.max(0, demandLb) / m.capacityLb) + 1),
    ),
  }));

  return { fleet: { truck_types }, molds, capacityAssumed };
}

/**
 * Lee la mezcla recomendada de la respuesta del motor.
 *
 * `fleet_usage` viene sin nombre ni id: su única llave es `capacity_kg`, y en
 * **kilogramos** aunque la petición viaje en libras. En vez de confiar en el
 * viaje de ida y vuelta del float, cada uso se empareja con el molde más cercano
 * — que es el número que el operador reconoce, porque es la capacidad de un
 * camión que ya tiene.
 *
 * Solo se listan los tipos con `used > 0`: los disponibles y no usados son la
 * holgura del techo, no una recomendación.
 */
export function readFleetSizing(
  response: PlanResponse | null | undefined,
  molds: readonly SizingMold[],
  capacityAssumed: boolean,
): FleetSizing | undefined {
  if (!response) return undefined;

  const nearestMold = (capacityKg: number): number => {
    const lb = capacityKg * KG_TO_LB;
    if (!molds.length) return Math.round(lb);
    return molds.reduce((best, m) =>
      Math.abs(m.capacityLb - lb) < Math.abs(best.capacityLb - lb) ? m : best,
    ).capacityLb;
  };

  const mix = (response.fleet_usage ?? [])
    .filter((u) => (u?.used ?? 0) > 0)
    .map((u) => ({ capacityLb: nearestMold(u.capacity_kg), used: u.used }))
    .sort((a, b) => b.capacityLb - a.capacityLb);

  const unassigned = (response.unassigned ?? []).length;
  // `trucks_used` solo es significativo en modo `fleet`; si no vino, se deriva de
  // la mezcla en vez de asumir cero.
  const trucks =
    response.trucks_used ?? mix.reduce((n, m) => n + m.used, 0);

  return { trucks, mix, isMinimum: unassigned > 0, unassigned, capacityAssumed };
}
